<div class="admin-dashboard min-h-screen bg-gray-100 p-6">
  <!-- Header -->
  <div class="dashboard-header mb-8 bg-white rounded-lg shadow-sm p-6">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <span class="fe fe-bar-chart-2 text-primary"></span>
          Platform Dashboard
        </h1>
        <p class="text-sm text-gray-600 mt-1">
          Real-time insights into  BusPro platform
        </p>
      </div>
      
      <div class="flex items-center space-x-3">
        <!-- Refresh Button -->
        <button
          (click)="refreshDashboard()"
          class="btn btn-outline btn-sm gap-2"
          [class.loading]="isLoading()"
          [disabled]="isLoading()"
        >
          <span class="fe fe-refresh-cw"></span>
          Refresh
        </button>

        <!-- Export Button -->
        <button
          (click)="exportDashboard()"
          class="btn btn-outline btn-sm gap-2"
        >
          <span class="fe fe-download"></span>
          Export
        </button>

        <!-- Settings Button -->
        <button class="btn btn-ghost btn-sm btn-circle">
          <span class="fe fe-settings text-lg"></span>
        </button>
      </div>
    </div>
  </div>

  <!-- Needs attention: overlapping stop zones silently cost a crossing its video AND its
       audit evidence, and nothing else on this page would ever mention it. The component renders
       nothing when there is nothing to act on, so no empty strip is left behind. -->
  <buspro-admin-geofence-conflicts-widget class="mb-6 block"></buspro-admin-geofence-conflicts-widget>

  <!-- Main Content -->
  <!-- Hero Section: Platform Overview -->
  <section class="hero-section mb-8">
    <!-- Top Row: Smaller width widgets + Revenue -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
      <!-- Total Buses Card (Half Width) - Temporarily commented out -->
      <!-- <div class="stat-card bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-2">
        @if (isLoading() && !dashboardStats()) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          @if (dashboardStats(); as stats) {
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Total Buses</p>
                <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">{{ stats.buses.total | number }}</h3>
                <div class="flex items-center space-x-2">
                  <span class="text-xs opacity-70">Total across platform</span>
                </div>
              </div>
              <div class="stat-icon">
                <span class="fe fe-truck text-5xl opacity-20"></span>
              </div>
            </div>
          }
        }
      </div> -->

      <!-- Active Trips Card (Half Width) -->
      <div class="stat-card bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-2">
        @if (isLoading() && !dashboardStats()) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          @if (dashboardStats(); as stats) {
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Active Trips</p>
                <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">{{ stats.operations.activeTrips | number }}</h3>
                <div class="flex items-center space-x-2">
                  <span class="text-xs opacity-70">Currently active</span>
                </div>
              </div>
              <div class="stat-icon">
                <span class="fe fe-navigation text-5xl opacity-20"></span>
              </div>
            </div>
          }
        }
      </div>

      <!-- Completed Trips Card (Next to Active Trips) -->
      <div class="stat-card bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-3">
        @if (isLoading() && !dashboardStats()) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          @if (dashboardStats(); as stats) {
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Completed Trips Today</p>
                <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">{{ stats.completedTrips.today | number }}</h3>
                <div class="flex items-center space-x-2">
                  @if (stats.completedTrips.growth > 0) {
                    <span class="comparison-badge comparison-up text-white bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-trending-up"></span>
                      {{ stats.completedTrips.growth | number:'1.1-1' }}%
                    </span>
                  } @else if (stats.completedTrips.growth < 0) {
                    <span class="comparison-badge comparison-down text-white bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-trending-down"></span>
                      {{ stats.completedTrips.growth | number:'1.1-1' }}%
                    </span>
                  } @else {
                    <span class="comparison-badge text-white/80 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-minus"></span>
                      0%
                    </span>
                  }
                  <span class="text-xs opacity-70">vs yesterday ({{ stats.completedTrips.yesterday | number }})</span>
                </div>
              </div>
              <div class="stat-icon">
                <span class="fe fe-check-circle text-5xl opacity-20"></span>
              </div>
            </div>
          }
        }
      </div>

      <!-- Online Tenant Users Card -->
      <div class="stat-card bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-2">
        @if (onlineTenantUsersStats().isLoading && onlineTenantUsersStats().totalUsers === 0) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Online Tenant Users</p>
              <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">
                {{ onlineTenantUsersStats().totalUsers | number }}
              </h3>
              <div class="flex items-center space-x-2">
                <span class="text-xs opacity-70">
                  Across {{ onlineTenantUsersStats().tenantCount }} tenant(s)
                </span>
              </div>
            </div>
            <div class="stat-icon">
              <span class="fe fe-users text-5xl opacity-20"></span>
            </div>
          </div>
        }
      </div>

      <!-- Average Audit Duration Card (Moved to top row) -->
      <div class="stat-card bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-2">
        @if (isLoading() && !dashboardStats()) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          @if (dashboardStats(); as stats) {
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Average Audit Duration</p>
                <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">{{ formatAuditDuration(stats.averageAuditDuration) }}</h3>
                <div class="flex items-center space-x-2">
                  <span class="text-xs opacity-70">Last 30 days</span>
                </div>
              </div>
              <div class="stat-icon">
                <span class="fe fe-clock text-5xl opacity-20"></span>
              </div>
            </div>
          }
        }
      </div>

      <!-- Today's Revenue Card (Half Width - 3 columns) -->
      <div class="stat-card bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 p-6 lg:col-span-3">
        @if (isLoading() && !dashboardStats()) {
          <div class="flex items-center justify-center h-full min-h-[120px]">
            <span class="loading loading-spinner loading-lg text-white"></span>
          </div>
        } @else {
          @if (dashboardStats(); as stats) {
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium opacity-90 mb-2 tracking-wide uppercase">Today's Revenue</p>
                <h3 class="stat-value text-4xl font-extrabold mb-3 tracking-tight">{{ stats.operations.revenue | currency:'UGX ':'symbol':'1.0-0' }}</h3>
                <div class="flex items-center space-x-2">
                  @if (stats.operations.revenueGrowth > 0) {
                    <span class="comparison-badge comparison-up text-white bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-trending-up"></span>
                      {{ stats.operations.revenueGrowth | number:'1.1-1' }}%
                    </span>
                  } @else if (stats.operations.revenueGrowth < 0) {
                    <span class="comparison-badge comparison-down text-white bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-trending-down"></span>
                      {{ stats.operations.revenueGrowth | number:'1.1-1' }}%
                    </span>
                  } @else {
                    <span class="comparison-badge text-white/80 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-full font-semibold flex items-center gap-1.5 text-xs">
                      <span class="fe fe-minus"></span>
                      0%
                    </span>
                  }
                  <span class="text-xs opacity-70">vs yesterday ({{ stats.operations.yesterdayRevenue | currency:'UGX ':'symbol':'1.0-0' }})</span>
                </div>
              </div>
              <div class="stat-icon">
                <span class="fe fe-dollar-sign text-5xl opacity-20"></span>
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- System Health Indicator (Simplified) -->
    <div class="mt-6 p-3 bg-white rounded-lg shadow-sm border border-gray-100">
      @if (isLoading() && !dashboardStats()) {
        <div class="flex items-center justify-center h-full min-h-[50px]">
          <span class="loading loading-spinner loading-sm"></span>
        </div>
      } @else {
        @if (dashboardStats(); as stats) {
          <div class="flex items-center justify-between flex-wrap gap-3">
            <div class="flex items-center space-x-2">
              <div class="w-2.5 h-2.5 rounded-full" [class]="'bg-' + healthStatusColor + '-500'"></div>
              <span class="text-sm font-medium text-gray-700">System Health</span>
              <span class="text-sm font-semibold" [class]="'text-' + healthStatusColor + '-600'">
                {{ healthStatusText }}
              </span>
            </div>

            <div class="flex items-center gap-4 text-sm">
              <div class="flex items-center gap-1.5">
                <span class="text-gray-500">Uptime:</span>
                <span class="font-semibold text-gray-900">{{ stats.systemHealth.uptime }}%</span>
              </div>

              <div class="flex items-center gap-1.5">
                <span class="text-gray-500">API Status:</span>
                <nz-badge [nzStatus]="'success'" [nzText]="stats.systemHealth.apiStatus" class="text-xs"></nz-badge>
              </div>

              <div class="flex items-center gap-1.5">
                <span class="text-gray-500">Database:</span>
                <nz-badge [nzStatus]="'success'" [nzText]="stats.systemHealth.dbStatus" class="text-xs"></nz-badge>
              </div>
            </div>
          </div>
        }
      }
    </div>
  </section>

    <!-- Main Grid Layout -->
    <div class="dashboard-grid grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Left Column (40%) -->
      <div class="lg:col-span-5 space-y-6">
        <!-- Tenant Health Matrix -->
        <nz-card nzTitle="🏢 Tenant Health Matrix" [nzExtra]="tenantHealthExtra" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <ng-template #tenantHealthExtra>
            <button 
              nz-button 
              nzType="text" 
              nzSize="small"
              (click)="refreshDashboard()"
              [class.loading]="isLoading()"
              [disabled]="isLoading()"
              title="Refresh tenant statistics"
            >
              <span class="fe fe-refresh-cw"></span>
            </button>
          </ng-template>
          
          @if (isLoading() && !dashboardStats()) {
            <div class="flex items-center justify-center py-8">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
          } @else {
            @if (dashboardStats(); as stats) {
              <div class="space-y-4">
                <!-- Total Tenants Summary -->
                <div class="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <div class="flex items-center justify-between">
                    <div>
                      <p class="text-sm font-medium text-gray-600">Total Tenants</p>
                      <p class="text-2xl font-bold text-gray-900">{{ stats.tenants.total | number }}</p>
                    </div>
                    <div class="text-right">
                      <p class="text-xs text-gray-500">Platform-wide</p>
                    </div>
                  </div>
                </div>
                
                <!-- Tenant Status Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <!-- Active Tenants -->
                  <div class="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-semibold text-gray-700">Active</span>
                      <span class="fe fe-check-circle text-green-600"></span>
                    </div>
                    <p class="text-2xl font-bold text-green-700 mb-1">{{ stats.tenants.active | number }}</p>
                    <p class="text-xs text-gray-600">Operating normally</p>
                    @if (stats.tenants.total > 0) {
                      <p class="text-xs text-gray-500 mt-1">
                        {{ ((stats.tenants.active / stats.tenants.total) * 100) | number:'1.1-1' }}% of total
                      </p>
                    }
                  </div>
                  
                  <!-- Provisioning Tenants -->
                  <div class="p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 hover:shadow-md transition-shadow">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-semibold text-gray-700">Provisioning</span>
                      <span class="fe fe-loader text-yellow-600 animate-spin"></span>
                    </div>
                    <p class="text-2xl font-bold text-yellow-700 mb-1">{{ stats.tenants.provisioning | number }}</p>
                    <p class="text-xs text-gray-600">In setup phase</p>
                    @if (stats.tenants.total > 0) {
                      <p class="text-xs text-gray-500 mt-1">
                        {{ ((stats.tenants.provisioning / stats.tenants.total) * 100) | number:'1.1-1' }}% of total
                      </p>
                    }
                  </div>
                  
                  <!-- Suspended Tenants -->
                  <div class="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200 hover:shadow-md transition-shadow">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-semibold text-gray-700">Suspended</span>
                      <span class="fe fe-pause-circle text-red-600"></span>
                    </div>
                    <p class="text-2xl font-bold text-red-700 mb-1">{{ stats.tenants.suspended | number }}</p>
                    <p class="text-xs text-gray-600">Need attention</p>
                    @if (stats.tenants.total > 0) {
                      <p class="text-xs text-gray-500 mt-1">
                        {{ ((stats.tenants.suspended / stats.tenants.total) * 100) | number:'1.1-1' }}% of total
                      </p>
                    }
                  </div>
                  
                  <!-- Terminated Tenants -->
                  <div class="p-4 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-semibold text-gray-700">Terminated</span>
                      <span class="fe fe-x-circle text-gray-600"></span>
                    </div>
                    <p class="text-2xl font-bold text-gray-700 mb-1">{{ stats.tenants.terminated | number }}</p>
                    <p class="text-xs text-gray-600">Permanently closed</p>
                    @if (stats.tenants.total > 0) {
                      <p class="text-xs text-gray-500 mt-1">
                        {{ ((stats.tenants.terminated / stats.tenants.total) * 100) | number:'1.1-1' }}% of total
                      </p>
                    }
                  </div>
                </div>
              </div>
            } @else {
              <nz-alert
                nzType="warning"
                nzMessage="No data available"
                nzDescription="Unable to load tenant statistics. Please try refreshing."
                nzShowIcon
              ></nz-alert>
            }
          }
        </nz-card>
      </div>

      <!-- Center Column (35%) -->
      <div class="lg:col-span-4 space-y-6">
        <!-- Resource Inventory -->
        <nz-card nzTitle="📦 Inventory Overview" [nzExtra]="inventoryExtra" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <ng-template #inventoryExtra>
            <a routerLink="/inventory/stocks" class="btn btn-sm btn-ghost">
              View All
            </a>
          </ng-template>
          
          @if (inventoryStatsQuery.data(); as inventory) {
            <div class="space-y-4">
              <!-- Wialon Devices -->
              <a routerLink="/inventory/stocks" class="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100 hover:shadow-md transition-shadow cursor-pointer">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-orange-500 rounded-lg">
                    <span class="fe fe-radio text-white text-xl"></span>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-700">Wialon Devices</p>
                    <p class="text-xs text-gray-500">Total: {{ inventory.wialonDevices.total }} • Available: {{ inventory.wialonDevices.available }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-2xl font-bold text-orange-600">{{ inventory.wialonDevices.available }}</p>
                  <p class="text-xs text-gray-500">available</p>
                </div>
              </a>

              <!-- SIM Cards -->
              <a routerLink="/inventory/stocks" class="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 hover:shadow-md transition-shadow cursor-pointer">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-green-500 rounded-lg">
                    <span class="fe fe-sim-card text-white text-xl"></span>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-700">SIM Cards</p>
                    <p class="text-xs text-gray-500">Total: {{ inventory.simCards.total }} • Available: {{ inventory.simCards.available }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-2xl font-bold text-green-600">{{ inventory.simCards.available }}</p>
                  <p class="text-xs text-gray-500">available</p>
                </div>
              </a>

              <!-- Android POS Devices -->
              <a routerLink="/inventory/stocks" class="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100 hover:shadow-md transition-shadow cursor-pointer">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-purple-500 rounded-lg">
                    <span class="fe fe-smartphone text-white text-xl"></span>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-700">Android POS Devices</p>
                    <p class="text-xs text-gray-500">Total: {{ inventory.posDevices.total }} • Available: {{ inventory.posDevices.available }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-2xl font-bold text-purple-600">{{ inventory.posDevices.available }}</p>
                  <p class="text-xs text-gray-500">available</p>
                </div>
              </a>

              <!-- MDVR Devices -->
              <a routerLink="/inventory/stocks" class="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100 hover:shadow-md transition-shadow cursor-pointer">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-blue-500 rounded-lg">
                    <span class="fe fe-router text-white text-xl"></span>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-700">MDVR Devices</p>
                    <p class="text-xs text-gray-500">Total: {{ inventory.mdvrDevices.total }} • Available: {{ inventory.mdvrDevices.available }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-2xl font-bold text-blue-600">{{ inventory.mdvrDevices.available }}</p>
                  <p class="text-xs text-gray-500">available</p>
                </div>
              </a>

              @if (inventory.lowStockAlerts > 0) {
                <nz-alert
                  nzType="warning"
                  [nzMessage]="'Low Stock Alert: ' + inventory.lowStockAlerts + ' item type(s) below threshold'"
                  nzDescription="Some inventory items are running low. Please review and restock."
                  nzShowIcon
                ></nz-alert>
              }
            </div>
          } @else if (inventoryStatsQuery.isPending()) {
            <div class="flex items-center justify-center py-8">
              <nz-spin nzSize="small"></nz-spin>
            </div>
          } @else if (inventoryStatsQuery.isError()) {
            <nz-alert
              nzType="warning"
              nzMessage="Failed to load inventory stats"
              nzDescription="Please try refreshing the page."
              nzShowIcon
            ></nz-alert>
          } @else {
            <nz-alert
              nzType="info"
              nzMessage="No inventory data available"
              nzShowIcon
            ></nz-alert>
          }
        </nz-card>

        <!-- Activity Feed -->
        <nz-card nzTitle="📡 Real-Time Activity Feed" [nzExtra]="activityExtra" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <ng-template #activityExtra>
            <nz-badge [nzDot]="true" nzStatus="processing">
              <span class="text-xs">Live</span>
            </nz-badge>
          </ng-template>
          
          <nz-alert
            nzType="info"
            nzMessage="Coming Soon"
            nzDescription="Live stream of platform activities including tenant registrations, bus additions, and system events."
            nzShowIcon
          ></nz-alert>
        </nz-card>

        <!-- System Alerts -->
        <nz-card nzTitle="🚨 System Alerts" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <nz-alert
            nzType="info"
            nzMessage="Coming Soon"
            nzDescription="Critical alerts, warnings, and notifications requiring admin attention."
            nzShowIcon
          ></nz-alert>
        </nz-card>
      </div>

      <!-- Right Column (25%) -->
      <div class="lg:col-span-3 space-y-6">
        <!-- Quick Actions -->
        <nz-card nzTitle="⚡ Quick Actions" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <div class="grid grid-cols-2 gap-3">
            <button 
              (click)="onRegisterTenant()"
              class="btn btn-outline btn-sm gap-2 flex-col h-20 hover:scale-105 transition-all duration-200 cursor-pointer"
            >
              <span class="fe fe-plus-circle text-xl"></span>
              <span class="text-xs">Register Tenant</span>
            </button>
            
            <button 
              (click)="onProvisionResources()"
              class="btn btn-outline btn-sm gap-2 flex-col h-20 hover:scale-105 transition-all duration-200 cursor-pointer"
            >
              <span class="fe fe-package text-xl"></span>
              <span class="text-xs">Provision</span>
            </button>
            
            <button 
              (click)="onAddBusType()"
              class="btn btn-outline btn-sm gap-2 flex-col h-20 hover:scale-105 transition-all duration-200 cursor-pointer"
            >
              <span class="fe fe-truck text-xl"></span>
              <span class="text-xs">Add Bus Type</span>
            </button>
            
            <button 
              (click)="onAddPlace()"
              class="btn btn-outline btn-sm gap-2 flex-col h-20 hover:scale-105 transition-all duration-200 cursor-pointer"
            >
              <span class="fe fe-map-pin text-xl"></span>
              <span class="text-xs">Add Place</span>
            </button>
          </div>
        </nz-card>

        <!-- Geographic Map -->
        <nz-card nzTitle="🗺️ Geographic Distribution" class="shadow-md hover:shadow-lg transition-shadow duration-300">
          <nz-alert
            nzType="info"
            nzMessage="Coming Soon"
            nzDescription="Interactive map showing tenant and bus distribution."
            nzShowIcon
          ></nz-alert>
        </nz-card>
      </div>
    </div>

    <!-- Bottom Section: Detailed Tables -->
    <div class="bottom-section mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Revenue Analytics -->
      <nz-card nzTitle="💰 Revenue Analytics" class="shadow-md hover:shadow-lg transition-shadow duration-300">
        <nz-alert
          nzType="info"
          nzMessage="Coming Soon"
          nzDescription="Revenue trends, top performers, and payment analytics."
          nzShowIcon
        ></nz-alert>
      </nz-card>

      <!-- Performance Metrics -->
      <nz-card nzTitle="📈 Performance Metrics" class="shadow-md hover:shadow-lg transition-shadow duration-300">
        <nz-alert
          nzType="info"
          nzMessage="Coming Soon"
          nzDescription="System performance metrics including API response times and database performance."
          nzShowIcon
        ></nz-alert>
      </nz-card>
    </div>

    <!-- Error State (only shown if query fails and no data available) -->
    @if (platformOverviewQuery.isError() && !dashboardStats()) {
      <div class="mt-8">
        <nz-alert
          nzType="error"
          nzMessage="Failed to load dashboard data"
          [nzDescription]="getErrorMessage()"
          nzShowIcon
          [nzAction]="errorAction"
        >
          <ng-template #errorAction>
            <button nz-button nzType="default" (click)="refreshDashboard()">
              Retry
            </button>
          </ng-template>
        </nz-alert>
      </div>
    }

    <!-- Quick Actions Modals -->
    <buspro-admin-register-tenant-modal
      [isVisible]="showRegisterTenantModal()"
      (cancelled)="onRegisterTenantModalClosed()"
    ></buspro-admin-register-tenant-modal>

    <buspro-admin-provision-resources-modal
      [visible]="showProvisionResourcesModal()"
      (cancelled)="onProvisionResourcesModalClosed()"
    ></buspro-admin-provision-resources-modal>

    <buspro-admin-add-standard-place
      [show]="showAddPlaceModal()"
      [defaultPlaceType]="PlaceTypeEnum.Terminal"
      (hide)="onAddPlaceModalClosed()"
      (refresh)="onAddPlaceModalClosed()"
    ></buspro-admin-add-standard-place>
</div>

